import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOnboarding as useLegacyOnboarding } from '@is24/tenant-network-fe-legacy/onboarding-widget';
import {
  IS24,
  ONBOARDING_QUERY_PARAM,
  ONBOARDING_QUERY_VALUE,
  resolveIS24BaseUrl,
  resolveListingState,
} from './mieterNetzwerk.utils';
import { tenantNetworkClient } from '@/tenantNetwork/client';
import { useGetTenantNetworkListing, useGetTenantNetworkStatus } from '@/apiHooks/tenantNetwork.hook';
import { useFeature } from '@/apiHooks/featureSwitches.hook';
import { isFeatureFlagEnabled } from '@/utils/isFeatureFlagEnabled.util';
import { Feature } from '@/@types/featureSwitch';

export const useIsTenantNetworkEnabled = () => {
  const remote = useFeature(Feature.TENANT_NETWORK_WIDGET);
  const local = isFeatureFlagEnabled('TENANT_NETWORK_WIDGET');
  return remote || local;
};

export const useTenantNetworkController = (isVisible: boolean, onClose: () => void) => {
  const isShadowDomEnabled =
    useFeature(Feature.TENANT_NETWORK_SHADOW_DOM) || isFeatureFlagEnabled(Feature.TENANT_NETWORK_SHADOW_DOM);
  const { initializing, tenantNetworkStatus } = useGetTenantNetworkStatus(isVisible);
  const propertyHubId = tenantNetworkStatus?.propertyHubId ?? null;

  const { tenantNetworkListing } = useGetTenantNetworkListing(
    propertyHubId,
    isVisible && tenantNetworkStatus !== undefined && !!propertyHubId,
  );

  const listingState = useMemo(
    () => resolveListingState(tenantNetworkStatus?.status, tenantNetworkListing?.status),
    [tenantNetworkStatus?.status, tenantNetworkListing?.status],
  );

  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isVisible && isShadowDomEnabled) tenantNetworkClient.prefetch('onboarding').catch(() => undefined);
  }, [isShadowDomEnabled, isVisible]);

  const clearOnboardingQueryParam = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete(ONBOARDING_QUERY_PARAM);
    window.history.replaceState({}, '', url);
  }, []);

  const handleOnboardingClose = useCallback(() => {
    clearOnboardingQueryParam();
    setIsOnboardingActive(false);
    onClose();
  }, [clearOnboardingQueryParam, onClose]);

  const { open: openLegacyOnboarding } = useLegacyOnboarding({
    coreCssVersion: '8.4.0' as const,
    type: 'PROPERTY_HUB' as const,
    onClose: handleOnboardingClose,
    onComplete: handleOnboardingClose,
  });

  const handleOpenMeinImmobilien = useCallback(() => {
    const path = propertyHubId ? `${IS24.PATHS.meinImmobilien}/${propertyHubId}` : IS24.PATHS.meinImmobilienOverview;
    window.open(`${resolveIS24BaseUrl()}${path}`, '_blank', 'noopener,noreferrer');
  }, [propertyHubId]);

  const handleOpenHomepage = useCallback(() => {
    window.open(`${resolveIS24BaseUrl()}${IS24.PATHS.homepage}`, '_blank', 'noopener,noreferrer');
  }, []);

  const handleStartOnboarding = useCallback(async () => {
    const url = new URL(window.location.href);
    url.searchParams.set(ONBOARDING_QUERY_PARAM, ONBOARDING_QUERY_VALUE);
    window.history.replaceState({}, '', url);
    setIsOnboardingActive(true);

    if (!isShadowDomEnabled) {
      openLegacyOnboarding();
      return;
    }

    setIsWidgetLoading(true);
    try {
      await tenantNetworkClient.openOnboarding({
        type: 'PROPERTY_HUB',
        onClose: handleOnboardingClose,
        onComplete: handleOnboardingClose,
      });
    } catch {
      clearOnboardingQueryParam();
      if (isMountedRef.current) {
        setIsOnboardingActive(false);
      }
    } finally {
      if (isMountedRef.current) {
        setIsWidgetLoading(false);
      }
    }
  }, [clearOnboardingQueryParam, handleOnboardingClose, isShadowDomEnabled, openLegacyOnboarding]);

  return {
    initializing,
    listingState,
    isOnboardingActive,
    isWidgetLoading,
    handleOpenMeinImmobilien,
    handleOpenHomepage,
    handleStartOnboarding,
  };
};
